import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Webcam from "react-webcam";
import {
  CameraOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Tooltip } from "antd";

const Camera = props => {
  const {
    setIsCaptureSuccess,
    isCaptureSuccess,
    isShowVideo,
    setIsShowVideo,
    setIsOpenCamera,
    setFileUploadImgList,
  } = props;
  const webcamImgRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState([]);
  const [isVideoRecord, setIsVideoRecord] = useState(false);
  const [isPause, setIsPause] = useState(false);
  const [timeRecord, setTimeRecord] = useState(0);
  let intervalId;
  let currentTime = 0;

  const handleStartCaptureClick = useCallback(() => {
    setIsVideoRecord(true);
    setTimeRecord(0);
    intervalId = setInterval(() => {
      currentTime = currentTime + 1;
      setTimeRecord(currentTime);
    }, 1000);
    mediaRecorderRef.current = new MediaRecorder(webcamImgRef.current.stream, {
      mimeType: "video/webm",
    });
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.start();
  }, [webcamImgRef, setIsVideoRecord, mediaRecorderRef]);

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        let newFile = new File([data], uuid() + ".mp4", {
          type: "video/mp4",
        });
        let fileInfo = {
          id: uuid(),
          name: newFile.name,
          file: newFile,
          url: URL.createObjectURL(newFile),
          type: newFile.type.startsWith("video") ? "video" : "file",
        };
        setFile(fileInfo);
      }
    },
    [setIsVideoRecord]
  );

  const handleStopCaptureClick = useCallback(() => {
    mediaRecorderRef.current.stop();
    setIsVideoRecord(false);
    setIsCaptureSuccess(true);
    clearInterval(intervalId);
  }, [mediaRecorderRef, webcamImgRef, setIsVideoRecord]);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user",
  };

  const handlePause = useCallback(() => {
    mediaRecorderRef.current.pause();
    setTimeRecord(currentTime);
    clearInterval(intervalId);
    setIsPause(true);
  }, [mediaRecorderRef]);

  const handleResume = useCallback(() => {
    mediaRecorderRef.current.resume();
    intervalId = setInterval(() => {
      currentTime = currentTime + 1;
      setTimeRecord(currentTime);
    }, 1000);
    setIsPause(false);
  }, [mediaRecorderRef]);

  const handleRetake = () => {
    setIsCaptureSuccess(false);
    setTimeRecord(0);
    setFile(null);
  };

  const convertBase64ToFile = async (url, filename, mimeType) => {
    if (url.startsWith("data:")) {
      const arr = url.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[arr.length - 1]);
      let n = bstr.length;
      // eslint-disable-next-line no-undef
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], filename, { type: mime || mimeType });
      return Promise.resolve(file);
    }
    return fetch(url)
      .then(res => res.arrayBuffer())
      .then(buf => new File([buf], filename, { type: mimeType }));
  };

  const capture = useCallback(() => {
    const imageSrc = webcamImgRef.current.getScreenshot();
    convertBase64ToFile(imageSrc, uuid(), "image/png").then(file => {
      let fileInfo = {
        id: uuid(),
        name: file.name,
        file: file,
        url: URL.createObjectURL(file),
        type: file.type.startsWith("image") ? "image" : "file",
      };
      setFile(fileInfo);
      setIsCaptureSuccess(true);
    });
  }, [webcamImgRef]);

  const handleAddImgToList = async fileUpload => {
    if (fileUpload) {
      await setFileUploadImgList(currentUploadImgList => [
        ...currentUploadImgList,
        fileUpload,
      ]);
      await setIsOpenCamera(false);
    }
  };

  useEffect(() => {
    if (timeRecord === 60) {
      handleStopCaptureClick();
    }
  }, [timeRecord]);

  useEffect(() => {
    if (!webcamImgRef?.current) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [webcamImgRef?.current]);
  return (
    <>
      <div
        style={{
          padding: "12px",
        }}
      >
        {!isCaptureSuccess && (
          <>
            <Webcam
              ref={webcamImgRef}
              // audio={true}
              screenshotFormat="image/png"
              videoConstraints={videoConstraints}
              mirrored={true}
              style={{ width: "100%" }}
            />
            {!isVideoRecord && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 12,
                }}
              >
                <Tooltip
                  title={t("inbox.chatMessage.chatMessageFooter.capture")}
                  size="small"
                >
                  <Button
                    shape="circle"
                    size="large"
                    icon={
                      isShowVideo ? <VideoCameraOutlined /> : <CameraOutlined />
                    }
                    onClick={() => {
                      if (isShowVideo) {
                        handleStartCaptureClick();
                      } else {
                        capture();
                      }
                    }}
                    disabled={isLoading}
                    style={{
                      marginRight: 12,
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={t("inbox.chatMessage.chatMessageFooter.switchMode")}
                  size="small"
                >
                  <Button
                    shape="circle"
                    size="large"
                    icon={
                      isShowVideo ? <CameraOutlined /> : <VideoCameraOutlined />
                    }
                    onClick={() => {
                      setIsShowVideo(!isShowVideo);
                    }}
                    disabled={isLoading}
                  />
                </Tooltip>
              </div>
            )}

            {isVideoRecord && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  marginTop: 12,
                }}
              >
                {timeRecord > 0 && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 20,
                      fontWeight: 600,
                      paddingRight: 12,
                    }}
                  >{`0:${timeRecord}`}</span>
                )}
                <Tooltip
                  title={t("inbox.chatMessage.chatMessageFooter.capture")}
                  size="small"
                >
                  <Button
                    shape="circle"
                    size="large"
                    onClick={() => {
                      handleStopCaptureClick();
                    }}
                    style={{
                      marginLeft: 12,
                      marginRight: 12,
                      backgroundColor: "#A9122B",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "15px",
                        height: "15px",
                        backgroundColor: "#fff",
                        borderRadius: "4px",
                      }}
                    ></div>
                  </Button>
                </Tooltip>

                <Button
                  type="text"
                  shape="circle"
                  size="large"
                  icon={isPause ? <PlayCircleOutlined /> : <PauseOutlined />}
                  onClick={() => {
                    if (isPause) {
                      handleResume();
                    } else {
                      handlePause();
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
        {isCaptureSuccess && (
          <>
            {file?.type === "image" && (
              <div>
                <img
                  src={file?.url}
                  style={{ width: "100%", height: "auto" }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 12,
                  }}
                >
                  <Button
                    size="large"
                    onClick={() => {
                      handleRetake();
                    }}
                  >
                    {t("camera.retake")}
                  </Button>
                  <Button
                    size="large"
                    onClick={() => {
                      handleAddImgToList(file);
                    }}
                  >
                    {t("camera.usePhoto")}
                  </Button>
                </div>
              </div>
            )}
            {file?.type === "video" && (
              <div>
                <video
                  src={file?.url}
                  controls
                  style={{ width: "100%", height: "auto" }}
                ></video>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 12,
                  }}
                >
                  <Button
                    size="large"
                    onClick={() => {
                      handleRetake();
                    }}
                  >
                    {t("camera.retake")}
                  </Button>
                  <Button
                    size="large"
                    onClick={() => {
                      handleAddImgToList(file);
                    }}
                  >
                    {t("camera.usePhoto")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Camera;
